import { PhysicsWorld, Rapier } from "../Provider/RapierProvider";
import { Vector3, Quaternion, BufferGeometry, Float32BufferAttribute, PointsMaterial, Points } from 'three'

/**
 * Crée un monde physique avec Rapier et une gravité nulle.
 * @returns {Promise<rapier.World>} Une instance du monde physique.
 */
export async function createPhysicsWorld() {

    const rapier = await Rapier();

    const gravity = { x: 0.0, y: 0.0, z: 0.0 }
    const world = new rapier.World(gravity);

    return world;
}

/**
 * Crée un collider à partir de la géométrie d'un mesh et l'ajoute au monde physique.
 * @param {teeth} teeth - Le mesh Three.js dont la géométrie est utilisée pour le collider.
 * @returns {Promise<{ body: rapier.RigidBody, collider: rapier.Collider }>} 
 * Un objet contenant le corps rigide et le collider associé.
 */
export async function createCollider(teeth) {
    const rapier = await Rapier();
    const physicsWorld = await PhysicsWorld();

    const mesh = teeth.refObj;
    const geometry = mesh.geometry;

    const verticesArray = geometry.attributes.position.array;
    const vertices = Array.from(verticesArray);

    const indexArray = geometry.index.array
    const indices = Array.from(indexArray)

    const colliderDesc = rapier.ColliderDesc.trimesh(vertices, indices);
    colliderDesc.setActiveEvents(rapier.ActiveEvents['COLLISION_EVENTS']);
    colliderDesc.setActiveCollisionTypes(rapier.ActiveCollisionTypes['ALL']);

    const rigidBodyDesc = rapier.RigidBodyDesc.kinematicPositionBased();
    const rigidBody = physicsWorld.createRigidBody(rigidBodyDesc);
    const collider = physicsWorld.createCollider(colliderDesc, rigidBody);

    collider.teethRef = teeth; // Ajout d'une référence a la dent

    return {
        'body' : rigidBody,
        'collider' : collider
    };
}

/**
 * Synchronise la transformation d'un collider avec celle d'un mesh Three.js.
 * @param {rapier.RigidBody} body - Le corps rigide dont la transformation doit être mise à jour.
 * @param {THREE.Mesh} mesh - Le mesh dont la transformation est utilisée.
 */
export async function synchronizeColliderTransformation(body, mesh)
{
    if (!mesh.matrixWorld) return;

    const position = new Vector3();
    const quaternion = new Quaternion();
    const scale = new Vector3();
    mesh.matrixWorld.decompose(position, quaternion, scale);

    body.setTranslation({ x: position.x, y: position.y, z: position.z });

    body.setRotation({
        x: quaternion.x,
        y: quaternion.y,
        z: quaternion.z,
        w: quaternion.w
    });
}

/**
 * Affiche le rendu de débogage du monde physique en ajoutant des points représentant les colliders.
 * @param {THREE.Scene} scene - La scène Three.js où afficher le rendu de débogage.
 */
const debugScene = []

const disposeDebugScene = () => {
    debugScene.forEach(point => {
        if (point.geometry) point.geometry.dispose();
        if (point.material) point.material.dispose();
    });
    debugScene.length = 0;
}

export async function displayDebugRender(scene) {

    disposeDebugScene();

    const physicsWorld = await PhysicsWorld();
    physicsWorld.step()

    const world = await PhysicsWorld();
    const { vertices, colors } = world.debugRender();

    if (!vertices || !colors || vertices.length === 0 || colors.length === 0) {
        console.error("Aucune donnée de vertices ou de couleurs disponible.");
        return;
    }

    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new Float32BufferAttribute(vertices, 3));
    geometry.setAttribute('color', new Float32BufferAttribute(colors, 3));

    const material = new PointsMaterial({
        size: 1,
        vertexColors: true,
    });

    const points = new Points(geometry, material);

    scene.add(points);
    debugScene.push(points)
    console.log("Debug render ajouté à la scène.");
}